<script setup lang="tsx">
import type { OrdenationDropdownOption } from '~/components/Filters/OrdenationSelect';
import type { MenuFixedLevels } from '~/composables/useDragToBottom/types';
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { WishlistsState } from '~/composables/useWishlists/wishlists';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { Property } from '~/core/entities/property/Property';
import type { EventBusPayload } from '~/core/helpers/symbols';
import type { BaseClusterData } from '~/core/service/googleMaps/components/GmapClusterMarker';
import type GmapMapComponent from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import type { SectionName } from '~/types/mixpanel';
import type { H3ClusterId } from '~~/core/ports/repositories';

import { useEventBus } from '@vueuse/core';
import { Button } from '#components';
import { useEvents } from '~/composables/events/useEvents';
import { useAddresses } from '~/composables/useAddressesWrapper';
import { useDragToBottom } from '~/composables/useDragToBottom';
import { HEADER_OFFSET_CONFIG } from '~/composables/useDragToBottom/constants';
import { buildManagedListingTrackingContext } from '~/composables/useListingEventTracking';
import { useMapClusters } from '~/composables/useMapClusters/useMapClusters';
import {
	MapState,
	useMapController
} from '~/composables/useMapController/useMapController';
import { usePropertyResponsiveFilters } from '~/composables/usePropertyResponsiveFilters/usePropertyResponsiveFilters';
import { useShareCollection } from '~/composables/useShareCollection';
import { useWishlistPropertiesListing } from '~/composables/useWishlistPropertiesListing';
import { useWishlists } from '~/composables/useWishlists/wishlists';
import { defaultOrdenationOptions } from '~/constants';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { useNavigationStore } from '~/store/navigationStore';
import { cn } from '~/utils';
import { grammarInflection } from '~~/utils';
import { onMounted } from 'vue';

import MobileScrollToMapButton from '~/components/Buttons/MobileScrollToMapButton.vue';
import { ContainerFluid } from '~/components/ContainerFluid';
import PropertyListSection from '~/components/PropertyList/PropertyListSection.vue';
import { ShareCollectionModal } from '~/components/ShareCollectionModal';

import IconMaterialSymbolsLightStarOutline from '~icons/material-symbols-light/star-outline';

// Pré-carrega os dados de endereços para evitar delay no primeiro clique dos filtros
// O composable usa useAsyncData com server: false, então só executa no cliente
useAddresses();

interface WishlistViewProps {
	wishlistId?: string;
	wishlistData?: any;
	isOwnWishlist: boolean;
	showFavoriteIcons?: boolean;
	sectionName?: SectionName;
	cardSectionName?: SectionName;
}

interface WishlistViewEmits {
	'properties-loaded': [properties: Property[]];
	'fetching-changed': [isFetching: boolean];
}

const props = withDefaults(defineProps<WishlistViewProps>(), {
	showFavoriteIcons: true,
	sectionName: 'user collection',
	cardSectionName: 'card saved properties'
});
const emit = defineEmits<WishlistViewEmits>();
const navigationStore = useNavigationStore();
const { hasContextualHeader } = storeToRefs(navigationStore);

const route = useRoute();
const router = useRouter();

const isShareModalOpen = ref(false);

const currentWishlist = computed(() => props.wishlistData);
const isOwnWishlist = computed(() => props.isOwnWishlist);

const shareButtonVariant = computed(() => {
	return isOwnWishlist.value ? 'primary' : 'secondary';
});

const ownerName = computed(() => {
	return currentWishlist.value?.owner?.name || 'Alguém';
});

const wishlistIdRef = computed(() => props.wishlistId || '');
const isOwnerRef = computed(() => isOwnWishlist.value);
const ownerNameRef = computed(() => ownerName.value);
const { handleNativeShare } = useShareCollection(
	wishlistIdRef,
	isOwnerRef,
	ownerNameRef
);

const propertyListSectionRef = useTemplateRef<InstanceType<
	typeof PropertyListSection
> | null>('propertyListSectionRef');
// Current filters from URL
const filtersFromPath = PropertyFiltersPresenter.getFiltersFromPath(
	route.fullPath,
	undefined,
	'colecoes'
);

const filters = ref<PropertyFilters>({
	...filtersFromPath,
	wishlistId: props.wishlistId,
	showHideProperties: true,
	showUnpublishedProperties: true,
	sortBy: filtersFromPath.sortBy || 'wishlist_recent',
	orderBy: filtersFromPath.orderBy || 'desc'
} as PropertyFilters);

const wishlistId = computed(() =>
	props.isOwnWishlist ? 'minha-colecao' : props.wishlistId || ''
);

const {
	properties,
	isLoading,
	isEmpty,
	currentPage,
	totalCount,
	loadWishlistProperties,
	loadNextPage
} = useWishlistPropertiesListing({
	wishlistId, // Pass as ref for reactivity
	includeH3Clusters: true,
	filters: computed(() => filters.value)
});

// Bridge the API differences for compatibility
const isFetching = isLoading;
const hasProperties = computed(() => !isEmpty.value);
const filteredCount = computed(() => totalCount.value);
const isFetchingForTheFirstTime = computed(
	() =>
		isLoading.value && currentPage.value === 1 && properties.value.length === 0
);

// useWishlistPropertiesListing fixes perPage to 12 — keep in sync if it changes.
const WISHLIST_PER_PAGE = 12;

const trackingContext = computed<ListingTrackingContext>(() =>
	buildManagedListingTrackingContext({
		placement: props.isOwnWishlist ? 'minha_colecao' : 'colecao_compartilhada',
		filters: filters.value,
		filteredCount: filteredCount.value,
		page: currentPage.value,
		perPage: WISHLIST_PER_PAGE
	})
);

// Next page function
async function nextPage() {
	await loadNextPage();
}

// Update filters and refresh data
function updateFiltersManager(newFilters: Partial<PropertyFilters>) {
	filters.value = { ...filters.value, ...newFilters };

	// Update URL
	const newPath = PropertyFiltersPresenter.getPathFromFilters(
		filters.value,
		{},
		{
			page: 'colecoes',
			collection: props.isOwnWishlist ? 'minha-colecao' : props.wishlistId || ''
		}
	);

	router.push(newPath);

	// Scroll to top if needed
	propertyListSectionRef.value?.scrollToTop?.();

	// Refresh will be triggered automatically by the filters watcher
}

const optimisticallyRemovedIds = ref<Set<string>>(new Set());

watch(
	() => properties.value,
	(newProperties: Property[]) => {
		optimisticallyRemovedIds.value.clear();
		emit('properties-loaded', newProperties || []);
	}
);

const displayedProperties = computed(() =>
	properties.value.filter(p => !optimisticallyRemovedIds.value.has(p.id))
);

// Adjusted filtered count considering optimistically removed properties
const adjustedFilteredCount = computed(() => {
	return Math.max(0, filteredCount.value - optimisticallyRemovedIds.value.size);
});

const collectionInfoLabel = computed(() => {
	return `${adjustedFilteredCount.value} ${grammarInflection(adjustedFilteredCount.value, 'imóvel salvo', 'imóveis salvos')}`;
});

const collectionTitle = computed(() => {
	if (isOwnWishlist.value) {
		return 'Minha Coleção';
	}
	return currentWishlist.value?.title || '';
});

const mobilePropertyCountLabel = computed<string>(() => {
	const count = adjustedFilteredCount.value;
	const article = grammarInflection(count, 'o', 'os');
	const propertyWord = grammarInflection(count, 'imóvel', 'imóveis');
	const countString = adjustedFilteredCount.value > 1 ? count : '';

	return `Toque ou arraste para ver ${article} ${countString} ${propertyWord}`;
});

const menuFixedLevelsHeightCalculate = computed(() => {
	if (!hasContextualHeader.value) {
		return {
			1: 220,
			2: 122
		};
	}

	const contextualHeaderHeight = 51;
	return {
		1: 220 + contextualHeaderHeight,
		2: 122 + contextualHeaderHeight
	};
});

const {
	dragRef,
	shouldFixOnBottom,
	isDragging,
	currentY,
	menuBottomLevel,
	moveBackOneLevel,
	moveBackToInitialPosition,
	toggleToClosedWhenOpen,
	toggleToHalfOpenWhenClosed,
	isTransitioning
} = useDragToBottom({
	menuFixedLevels: menuFixedLevelsHeightCalculate,
	scrollContainer: propertyListSectionRef,
	disabled: computed(() => !hasProperties.value)
});

const isMenuOpen = computed(() => [0, 1].includes(menuBottomLevel.value));
const shouldShowMobilePropertyCountLabel = computed<boolean>(() => {
	return !!(menuBottomLevel.value === 2 && adjustedFilteredCount.value);
});

const mapState = useState<PropertiesListingMapState>(
	'properties-listing-map',
	() => ({
		fetchedData: null,
		filters: {
			transactionType: 'sell',
			countPluscodes: [],
			propertyTypes: [],
			locationSlug: 'sao-paulo-sp-brasil'
		},
		pluscodes: {},
		mapConfig: {
			zoom: 14,
			maxZoom: 17,
			minZoom: 8,
			bounds: [],
			center: {
				lat: -23.585,
				lng: -46.685
			}
		}
	})
);

// Mesmo motivo do PropertyModal: chunk do mapa fora do grafo de quem so
// importa a wishlist.
const GmapMap = defineAsyncComponent(
	() => import('~/core/service/googleMaps/components/GmapMap/GmapMap.vue')
);
// Filhos do mapa: mesma razao do GmapMap.
const GmapClusterMarker = defineAsyncComponent(
	() =>
		import('~/core/service/googleMaps/components/GmapClusterMarker/GmapClusterMarker.vue')
);
const GmapMarker = defineAsyncComponent(
	() => import('~/core/service/googleMaps/components/GmapMarker/GmapMarker.vue')
);

const gmapRef = ref<InstanceType<typeof GmapMapComponent> | null>(null);
const clusterMarkersRefs = ref<
	Map<string, InstanceType<typeof GmapClusterMarker>>
>(new Map());

const clickCount = ref(0);

const mapOptions = computed(() => ({
	gestureHandling: 'greedy',
	draggable: true,
	scrollwheel: true,
	disableDefaultUI: false,
	zoomControl: true
}));

// Map clusters wiring
const {
	clusters,
	selectedClustersData,
	selectedH3Ids,
	markersKey,
	updateClusters: originalUpdateClusters,
	handleClusterSelectionChanged: originalHandleClusterSelectionChanged,
	handleClusterDeselectedByResolution:
		originalHandleClusterDeselectedByResolution,
	normalizeClusterCoordinates,
	clearSelectedClusters
} = useMapClusters({
	gmapRef,
	clustersFilters: {
		wishlistId: props.wishlistId || '',
		showHideProperties: true,
		showUnpublishedProperties: true
	}
});

// Reactive wrapper that only returns clusters when map instance is available
const readyClusters = computed(() => {
	const mapInstance = gmapRef.value?.mapInstance;
	const currentClusters = clusters.value;

	if (!mapInstance) {
		return [];
	}

	return currentClusters;
});

const collectionName = computed(() => {
	// Prioriza a prop isOwnWishlist para determinar se é a coleção principal
	if (props.isOwnWishlist) {
		return 'minha-colecao';
	}

	// Fallback: Se a URL contém 'minha-colecao', sempre retorna isso
	if (route.path.includes('minha-colecao')) {
		return 'minha-colecao';
	}
	if (route.path.includes('/compartilhada/')) {
		return props.wishlistId;
	}

	return props.wishlistId || 'minha-colecao';
});

const mapController = useMapController({
	filters,
	clusters,
	gmapRef,
	selectedClustersData,
	normalizeClusterCoordinates,
	originalUpdateClusters,
	pageConfig: {
		page: 'colecoes',
		collection: computed(() =>
			props.isOwnWishlist ? 'minha-colecao' : props.wishlistId
		).value,
		wishlistId: props.wishlistId
	}
});

const shouldShowMap = computed(() => !mapController.state.isInitializing.value);

const {
	trackSelectionCleared,
	trackCtaSimilarPropertiesClicked,
	trackFormPropertyClicked
} = useEvents();

const totalSelectedPropertiesCount = computed(() => {
	let total = 0;
	for (const clusterId of selectedClustersData.value.keys()) {
		const cluster = clusters.value.find(c => c.geometry.id === clusterId);
		if (cluster) {
			total += cluster.properties.pointCount;
		}
	}
	return total;
});

const eventCleanupFunctions: (() => void)[] = [];

const propertyCardHoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardHover
);
const propertyCardUnhoverBus = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardUnhover
);

const contactBrokerClickBus = useEventBus(EventBusKeys.contactBrokerClick);
const similarPropertiesClickBus = useEventBus(
	EventBusKeys.similarPropertiesClick
);

const viewMoreBus = useEventBus(EventBusKeys.viewMoreClicked);

// Watch global wishlist state for automatic refresh
const wishlistsState = useState<WishlistsState>('wishlists');

// Flag to track component mount state - used to prevent stale nextTick callbacks
// from affecting the global map state after navigation
let isWishlistViewMounted = true;

// Watch global wishlist state changes to reload properties
// IMPORTANT: This watcher must be in the component's setup scope (not inside nextTick/setTimeout)
// to ensure it gets automatically cleaned up when the component is unmounted.
// Otherwise, it would continue running and affect other pages that share the global mapState.
watch(
	() => wishlistsState.value?.favoritePropertyIds ?? [],
	() => {
		// Only proceed if wishlistsState is properly initialized
		if (!wishlistsState.value) return;

		loadWishlistProperties();

		// Force clusters update when property is added/removed to reflect changes on map
		// Also trigger fit bounds to re-center map on remaining properties
		nextTick(() => {
			// CRITICAL: Check if component is still mounted before affecting global map state
			// Without this check, if user navigates away (e.g., from collection to SRP) before
			// nextTick executes, these map operations would affect the new page's map
			if (!isWishlistViewMounted) return;

			// Set state to APPLYING_FIT_BOUNDS before updating clusters
			// This ensures the map will re-center on remaining properties after removal
			mapController.initializeMapWithFitBounds();
			mapController.forceUpdateClusters(filters.value);
		});
	},
	{ deep: true }
);

eventCleanupFunctions.push(
	onClientEventBus(propertyCardHoverBus, handleCardHover),
	onClientEventBus(propertyCardUnhoverBus, handleCardUnhover),
	onClientEventBus(viewMoreBus, handleViewMoreClicked),
	onClientEventBus(contactBrokerClickBus, handleContactBrokerClick),
	onClientEventBus(similarPropertiesClickBus, handleSimilarPropertiesClick)
);

const propertyListSectionEl = computed(
	() => propertyListSectionRef?.value?.$el
);
const { y: scrollY } = useScroll(propertyListSectionEl);

watchThrottled(
	scrollY,
	() => {
		navigationStore.setPropertyListSectionScrollY(scrollY.value);
	},
	{ throttle: 200 }
);

async function updateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	// Força usar 'minha-colecao' quando é a própria coleção do usuário
	const currentCollection = props.isOwnWishlist
		? 'minha-colecao'
		: collectionName.value;

	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			{
				...filters.value,
				sortBy: event.sortBy,
				orderBy: event.orderBy
			},
			{},
			{ page: 'colecoes', collection: currentCollection }
		)
	);
}

const { $wishlistRefresh } = useNuxtApp();
const {
	trackReorderClicked,
	trackCtaViewMoreClicked,
	trackMapListingClicked,
	trackShareCollectionClicked,
	trackMapListingInteracted
} = useEvents();

const shouldShowNextPageButton = computed<boolean>(() => {
	if (isFetchingForTheFirstTime.value) return false;
	return displayedProperties.value.length < adjustedFilteredCount.value;
});

// Manage MobileScrollToMapButton visibility similar to SRP
const isViewMoreButtonInViewport = ref(false);
const viewMoreButtonPosition = ref<{ top: number } | undefined>();
const hasEmptyState = computed(
	() => !isFetchingForTheFirstTime.value && !hasProperties.value
);
const viewMoreButtonPositionComputation = computed(() => {
	if (hasEmptyState.value) return undefined;
	return viewMoreButtonPosition.value;
});

const mobileScrollButtonHideAfterElement = computed(() => {
	// For wishlist, hide the button when reaching the footer
	return propertyListSectionRef.value?.footerRef || null;
});

const headerDragOffsetY = useState<number>('header-drag-offset-y', () => 0);

const isHeaderHidden = computed(() => {
	return headerDragOffsetY.value >= HEADER_OFFSET_CONFIG.MAX_OFFSET;
});

const sectionHeightClass = computed(() => {
	if (!hasContextualHeader.value) {
		return 'h-[calc(100dvh-60px)]';
	}

	if (menuBottomLevel.value === 2) {
		return 'h-full';
	}

	if (isHeaderHidden.value) {
		return '!h-[calc(100dvh-51px)] lg:!h-[calc(100dvh-80px)]';
	}

	if (isOwnWishlist.value) {
		return 'h-full lg:!h-[calc(100dvh-111px)]';
	}

	return '!h-[calc(100dvh-51px)] lg:!h-[calc(100dvh-80px)]';
});

const emptyStateData = computed(() => {
	if (!hasEmptyState.value) return null;

	return {
		title: 'Sem imóveis salvos por aqui',
		description:
			'Sua coleção não tem imóveis nesta região. Volte para a visão geral dos favoritos.',
		icon: IconMaterialSymbolsLightStarOutline
	};
});

const mapSectionClasses = computed(() => {
	const baseClasses =
		'absolute h-[calc(100dvh-80px)] w-full md:h-[calc(100dvh-120px)] lg:relative';

	// Use different height for shared vs own collection
	if (isOwnWishlist.value) {
		return `${baseClasses} lg:h-[calc(100dvh-111px)]`;
	} else {
		return `${baseClasses} lg:h-[calc(100dvh-80px)]`;
	}
});

function handleViewMoreButtonVisibilityChanged(isVisible: boolean) {
	isViewMoreButtonInViewport.value = isVisible;
}

function handleViewMoreButtonPositionChanged(position: { top: number }) {
	viewMoreButtonPosition.value = position;
}

function getClusterKey(clusterId: string): string {
	const isSelected = selectedClustersData.value.has(clusterId);

	if (isSelected) {
		return `selected-${clusterId}`;
	} else {
		return `unselected-${markersKey.value}-${clusterId}`;
	}
}

function handleViewMoreClicked(page: string) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: page,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CLICK_COUNT]: clickCount.value++
	});
}

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	trackReorderClicked({
		[MixpanelProps.CLICK_EXCLUSIVES]: value === 'pilar_exclusivities',
		[MixpanelProps.CLICK_HIGHLIGHTS]: value === 'highlights',
		[MixpanelProps.CLICK_LOWEST_PRICE]: value === 'asking_price_asc',
		[MixpanelProps.CLICK_HIGHEST_PRICE]: value === 'asking_price_desc',
		[MixpanelProps.CLICK_FEWER_PHOTOS]: value === 'images_count_asc',
		[MixpanelProps.CLICK_MORE_PHOTOS]: value === 'images_count_desc',
		[MixpanelProps.CLICK_MORE_RELEVANT]: value === 'default_ordenation',
		[MixpanelProps.CLICK_MORE_RECENT]: value === 'wishlist_recent',
		[MixpanelProps.CLICK_OLDEST]: value === 'wishlist_oldest'
	});
}

function setClusterMarkerRef(
	clusterId: string,
	markerRef: InstanceType<typeof GmapClusterMarker> | null
) {
	if (markerRef && '$el' in markerRef) {
		clusterMarkersRefs.value.set(clusterId, markerRef);
	} else {
		clusterMarkersRefs.value.delete(clusterId);
	}
}

const markerPosition = computed<{ lat: number; lng: number } | null>(() => {
	if (!filters.value.poiLocationLat || !filters.value.poiLocationLng)
		return null;
	return {
		lat: filters.value.poiLocationLat,
		lng: filters.value.poiLocationLng
	};
});

function handleOpenMapVisionButtonClick(clickLocation: 'cta' | 'map') {
	trackMapListingClicked({
		current_page: 'listed properties',
		click_location: clickLocation,
		cta_text: 'Ver imóveis no Mapa',
		destination_url: PropertyFiltersPresenter.getPathFromFilters(
			filters.value,
			{},
			{ page: 'mapa-de-imoveis' }
		),
		property_count: adjustedFilteredCount.value,
		filter_city: filters.value.cities?.[0],
		filter_region: filters.value.macroregions?.[0],
		filter_neighborhood: filters.value.regions?.[0]
	});
}

async function handleOpenMapFromMobileButton() {
	await propertyListSectionRef.value?.scrollToTop();
	toggleToClosedWhenOpen();
	handleOpenMapVisionButtonClick('cta');
}

function handleMenuBottomLevelClick() {
	if (isDragging.value) return;
	moveBackOneLevel();
}

function handleTrackShareCollectionClicked() {
	trackShareCollectionClicked({
		[MixpanelProps.SHARE_WHATSAPP]: false,
		[MixpanelProps.SHARE_FACEBOOK]: false,
		[MixpanelProps.COPY_LINK]: false,
		[MixpanelProps.SHARE_EMAIL]: false,
		[MixpanelProps.SHARE_TWITTER]: false,
		[MixpanelProps.PROPERTY_COUNT]: adjustedFilteredCount.value,
		[MixpanelProps.SHARE_CLICKED]: true,
		[MixpanelProps.SECTION_NAME]: props.sectionName,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath
	});
}

function handleShareCollectionMobile() {
	handleNativeShare();
	handleTrackShareCollectionClicked();
}

function handleShareCollection() {
	isShareModalOpen.value = true;
	handleTrackShareCollectionClicked();
}

function handleExploreProperties() {
	navigateTo(route.path, { replace: true });

	// Force refresh clusters and reinitialize map after clearing query params
	nextTick(async () => {
		// Guard against stale nextTick execution after unmount
		if (!isWishlistViewMounted) return;

		mapController.initializeMapWithFitBounds();
		await mapController.forceUpdateClusters({
			...filters.value,
			bounds: undefined,
			zoom: undefined
		});
	});
}

function handleClusterSelectionChanged(params: {
	clusterId: string;
	isSelected: boolean;
}) {
	const cluster = clusters.value.find(c => c.geometry.id === params.clusterId);
	if (!cluster) return;

	originalHandleClusterSelectionChanged(params);
	nextTick(() => {
		trackMapListingInteracted({
			[MixpanelProps.CURRENT_PAGE]: 'Wishlist',
			[MixpanelProps.CLICK_SQUARES]: true,
			[MixpanelProps.ZOOM_CLICK]: false,
			[MixpanelProps.FINAL_CLICK]: true,
			[MixpanelProps.SQUARE_COUNT]: selectedClustersData.value.size || 1,
			[MixpanelProps.PROPERTY_COUNT]: totalSelectedPropertiesCount.value
		});
	});
}

function handleClusterDeselectedByResolution(data: { clusterId: string }) {
	originalHandleClusterDeselectedByResolution(data);

	nextTick(() => {
		trackMapListingInteracted({
			current_page: 'Wishlist',
			click_squares: true,
			zoom_click: false,
			final_click: true,
			square_count: selectedClustersData.value.size || 1,
			property_count: totalSelectedPropertiesCount.value
		});
	});
}

function handleCardHover(h3ClusterIds: H3ClusterId[]) {
	if (selectedClustersData.value.size === 1) {
		return;
	}

	const clusterId = h3ClusterIds.find(
		x => x.zoom === gmapRef.value?.mapInstance?.getZoom()
	)?.id;

	const cluster = clusters.value.find(
		cluster => cluster.geometry.id === clusterId
	);

	if (cluster) {
		const markerRef = clusterMarkersRefs.value.get(cluster.geometry.id);
		markerRef?.triggerMarkerHover();
	}
}

function handleCardUnhover(h3ClusterIds: H3ClusterId[]) {
	if (selectedClustersData.value.size === 1) {
		return;
	}

	const clusterId = h3ClusterIds.find(
		x => x.zoom === gmapRef.value?.mapInstance?.getZoom()
	)?.id;

	const cluster = clusters.value.find(
		cluster => cluster.geometry.id === clusterId
	);

	if (cluster) {
		const markerRef = clusterMarkersRefs.value.get(cluster.geometry.id);
		markerRef?.triggerMarkerUnhover();
	}
}

function handleContactBrokerClick(
	data: EventBusPayload<typeof EventBusKeys.contactBrokerClick>
) {
	clickCount.value += 1;
	trackFormPropertyClicked({
		[MixpanelProps.COMMERCIAL_ID]: data.commercialId,
		[MixpanelProps.CURRENT_PAGE]: 'Minha coleção',
		[MixpanelProps.CTA_TEXT]: 'Contactar',
		[MixpanelProps.ASKING_PRICE]: data.askingPrice,
		[MixpanelProps.SECTION_NAME]: props.cardSectionName,
		[MixpanelProps.AGENT_NAME]: data.agentName,
		[MixpanelProps.COMPANY_NAME]: data.companyName,
		[MixpanelProps.USER_STATUS]: 'logged in'
	});
}

function handleSimilarPropertiesClick(
	data: EventBusPayload<typeof EventBusKeys.similarPropertiesClick>
) {
	trackCtaSimilarPropertiesClicked({
		[MixpanelProps.COMMERCIAL_ID]: data.commercialId,
		[MixpanelProps.CURRENT_PAGE]: 'Minha coleção',
		[MixpanelProps.CTA_TEXT]: 'Semelhantes',
		[MixpanelProps.CLICK_CTA_CARD]: data.origin === 'card',
		[MixpanelProps.CLICK_CTA_FLOATING]: data.origin === 'floating',
		[MixpanelProps.CLICK_CTA_FIXED]: data.origin === 'fixed'
	});
}

async function handleClusterClicked(clusterData: BaseClusterData) {
	const cluster = clusters.value.find(
		c => c.geometry.id === clusterData.clusterId
	);
	if (!cluster) return;

	mapController.handleClusterNavigation();
	await propertyListSectionRef.value?.scrollToTop();
	toggleToHalfOpenWhenClosed();
	trackMapListingInteracted({
		current_page: 'Wishlist',
		click_squares: true,
		zoom_click: cluster.properties.pointCount > 20,
		final_click: cluster.properties.pointCount <= 20,
		square_count: selectedClustersData.value.size || 1,
		property_count: totalSelectedPropertiesCount.value
	});

	if (!gmapRef.value?.mapInstance) return;

	const idleListener = gmapRef.value.mapInstance.addListener('idle', () => {
		google.maps.event.removeListener(idleListener);

		nextTick(() => {
			const bounds = gmapRef.value?.mapInstance?.getBounds();
			if (bounds) {
				mapController.updateMapBounds(bounds);
			}
		});
	});
}

function handleClearSelectedClusters() {
	trackSelectionCleared({
		current_page: 'Wishlist',
		property_count: totalSelectedPropertiesCount.value,
		square_count: selectedClustersData.value.size || 1
	});

	clearSelectedClusters();
}

function updateUrlForH3Ids(newH3Ids: string[]): void {
	const newFilters = { ...filters.value, h3Ids: newH3Ids };
	const mapInstance = gmapRef.value?.mapInstance;
	const currentZoom = mapInstance?.getZoom();

	if (!newH3Ids?.length) {
		const currentBounds = mapInstance?.getBounds();

		if (currentBounds) {
			newFilters.bounds = getBoundsString(currentBounds);
		}
	}

	newFilters.zoom = currentZoom;

	// Força usar 'minha-colecao' quando é a própria coleção do usuário
	const currentCollection = props.isOwnWishlist
		? 'minha-colecao'
		: collectionName.value;
	router.push(
		PropertyFiltersPresenter.getPathFromFilters(
			newFilters,
			{},
			{
				page: 'colecoes',
				collection: currentCollection
			}
		)
	);
}

function getBoundsString(bounds: google.maps.LatLngBounds): string {
	const { north, south, east, west } = bounds.toJSON();
	return `${west},${south},${east},${north}`;
}

function selectClustersFromH3Ids(): void {
	if (!filters.value.h3Ids?.length || !clusters.value.length) {
		return;
	}

	const h3IdsSet = new Set(filters.value.h3Ids);

	clusters.value.forEach(cluster => {
		const clusterId = cluster.geometry.id;

		if (h3IdsSet.has(clusterId) && !selectedClustersData.value.has(clusterId)) {
			originalHandleClusterSelectionChanged({
				clusterId: cluster.geometry.id,
				isSelected: true
			});
		}
	});
}

// Watch for route changes to update filters
watch(
	() => route.fullPath,
	(newPath, oldPath) => {
		if (newPath !== oldPath) {
			// Extract new filters from the updated URL
			const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
				newPath,
				undefined,
				'colecoes'
			);

			// Update local filters to trigger reload
			filters.value = {
				...newFilters,
				wishlistId: props.wishlistId,
				showHideProperties: true,
				showUnpublishedProperties: true,
				sortBy: newFilters.sortBy || 'wishlist_recent',
				orderBy: newFilters.orderBy || 'desc'
			} as PropertyFilters;
		}
	}
);

onMounted(() => {
	loadWishlistProperties();
	mapController.handleInitialMapLoad();
});

// Cleanup cluster marker refs when markers change
watch(
	() => markersKey.value,
	() => {
		const selectedClusterIds = new Set(selectedClustersData.value.keys());

		const clustersToDelete = Array.from(clusterMarkersRefs.value.keys()).filter(
			clusterId => !selectedClusterIds.has(clusterId)
		);

		clustersToDelete.forEach(clusterId => {
			clusterMarkersRefs.value.delete(clusterId);
		});
	}
);

watch(selectedH3Ids, (newH3Ids, oldH3Ids) => {
	if (mapController.state.currentState.value !== MapState.READY) return;

	const hasChanges =
		JSON.stringify(newH3Ids) !== JSON.stringify(filters.value.h3Ids);

	if (hasChanges) {
		updateUrlForH3Ids(newH3Ids ?? []);

		const hasNewSelection = (newH3Ids?.length ?? 0) > (oldH3Ids?.length || 0);

		if (hasNewSelection) {
			toggleToHalfOpenWhenClosed();
		}
	}
});

watchOnce(clusters as any, (newClusters: any, oldClusters: any) => {
	const shouldAutoSelect =
		filters.value.h3Ids?.length &&
		newClusters.length > 0 &&
		(!oldClusters || oldClusters.length === 0);

	if (shouldAutoSelect) {
		const mapInstance = gmapRef.value?.mapInstance;

		if (mapInstance) {
			selectClustersFromH3Ids();
			const idleListener = mapInstance.addListener('idle', () => {
				google.maps.event.removeListener(idleListener);

				mapController.clearProgrammaticFlag();
			});
		}
	}
});

onUnmounted(() => {
	// Mark component as unmounted to prevent stale nextTick callbacks from affecting global state
	isWishlistViewMounted = false;

	eventCleanupFunctions.forEach(cleanup => cleanup());
	eventCleanupFunctions.length = 0;
	clusterMarkersRefs.value.clear();
});

const ordenationOptions: OrdenationDropdownOption[] = [
	{
		label: 'Mais recentes',
		value: 'wishlist_recent',
		sortBy: 'wishlist_recent',
		orderBy: 'desc'
	},
	{
		label: 'Menos recentes',
		value: 'wishlist_oldest',
		sortBy: 'wishlist_recent',
		orderBy: 'asc'
	},
	// Filter out "Mais Relevantes" option for wishlist
	...defaultOrdenationOptions.filter(
		option => option.value !== 'default_ordenation'
	)
];
</script>

<template>
	<div class="relative size-full">
		<MobileScrollToMapButton
			v-if="propertyListSectionRef"
			:filters="filters"
			:is-menu-open="menuBottomLevel === 0"
			:scroll-element="propertyListSectionRef"
			:is-view-more-button-in-viewport="isViewMoreButtonInViewport"
			:view-more-button-position="viewMoreButtonPositionComputation"
			:hide-after="mobileScrollButtonHideAfterElement"
			@open-map-vision="handleOpenMapFromMobileButton"
		/>

		<div class="size-full divide-y divide-white-dark-1 bg-white">
			<div class="size-full bg-black">
				<ContainerFluid
					:main-class="[
						'grid grid-cols-1 lg:grid-cols-[minmax(0,45%)_1fr] h-full xl:grid-cols-[minmax(0,60%)_1fr] 2xl:grid-cols-[minmax(0,820px)_1fr]',
						shouldFixOnBottom ? 'overflow-hidden' : ''
					]"
					class="h-full bg-white"
				>
					<div
						ref="dragRef"
						class="z-20 h-dvh bg-white pt-4 lg:!cursor-auto lg:pt-0"
					>
						<div
							id="drawer"
							class="pointer-events-none absolute top-0 z-30 flex h-[54px] w-full justify-center lg:hidden"
						>
							<div
								class="pointer-events-auto mt-[6px] h-1 w-12 rounded-full bg-white-dark-3"
							/>
						</div>
						<PropertyListSection
							ref="propertyListSectionRef"
							:class="
								cn([isMenuOpen ? '' : '!h-0 opacity-0', sectionHeightClass])
							"
							:is-fixed-bottom="shouldFixOnBottom"
							:is-user-dragging="isDragging"
							:is-fetching-for-the-first-time="isFetchingForTheFirstTime"
							:has-properties="hasProperties"
							:property-count-label="collectionTitle"
							:info-label="collectionInfoLabel"
							:filters="filters"
							:is-fetching="isFetching"
							:properties="displayedProperties"
							:should-show-next-page-button="shouldShowNextPageButton"
							:empty-state-data="emptyStateData"
							:breadcrumb-items="[]"
							:menu-bottom-level="menuBottomLevel"
							:current-y="currentY"
							:is-dragging="isDragging"
							:is-transitioning="isTransitioning"
							:ordenation-options="ordenationOptions"
							default-sort-by="wishlist_recent"
							default-order-by="desc"
							:section="props.cardSectionName"
							:show-card-actions="true"
							:tracking-context="trackingContext"
							container-classes="min-h-[auto]"
							ordenation-select-size="xs"
							ordenation-select-button-classes="size-10 p-[0.62rem] xl:py-1 xl:px-3 xl:h-10 lg:size-10 xl:w-auto flex gap-1 items-center justify-center text-sm font-medium leading-5 rounded-[0.125rem] [&_svg]:flex-shrink-0"
							count-label-classes="text-[24px] lg:text-[28px]"
							property-count-label-classes="lg:text-base"
							@update-ordenation="updateOrdenation"
							@reorder-clicked="handleReorderClicked"
							@next-page="nextPage"
							@view-more-button-visibility-changed="
								handleViewMoreButtonVisibilityChanged
							"
							@view-more-button-position-changed="
								handleViewMoreButtonPositionChanged
							"
						>
							<template #empty-state-cta>
								<Button
									variant="secondary"
									class="h-12 rounded-[0.125rem] px-5 py-3"
									@click="handleExploreProperties"
								>
									Mostrar toda a coleção
								</Button>
							</template>

							<template #actions>
								<!-- Mobile Share Button -->
								<Button
									size="xs"
									:variant="shareButtonVariant"
									class="flex size-10 items-center justify-center gap-2 rounded-[0.125rem] p-[0.63rem] xl:hidden xl:w-auto"
									@click="handleShareCollectionMobile"
								>
									<IconPilarSend48Degrees class="aspect-square size-5" />
									<span class="hidden text-sm">Compartilhar</span>
								</Button>

								<!-- Desktop Share Button -->
								<Button
									size="sm"
									:variant="shareButtonVariant"
									class="hidden h-10 w-full items-center gap-2 rounded-[0.125rem] xl:flex"
									@click="handleShareCollection"
								>
									<IconPilarSend48Degrees class="size-5" />
									<span class="hidden text-sm sm:inline lg:hidden xl:inline">
										Compartilhar
									</span>
								</Button>
							</template>
						</PropertyListSection>

						<div
							v-show="shouldShowMobilePropertyCountLabel"
							class="flex h-[500px] w-full items-start justify-start bg-white px-4 text-primary lg:hidden"
							@click="handleMenuBottomLevelClick"
						>
							<div class="flex items-center">
								<IconMaterialSymbolsLightChevronLeft
									class="mr-2 size-6 rotate-90"
								/>
								<span class="text-sm font-medium">
									{{ mobilePropertyCountLabel }}
								</span>
							</div>
						</div>

						<div
							v-show="
								!adjustedFilteredCount && !shouldShowMobilePropertyCountLabel
							"
							class="flex h-[500px] w-full items-start justify-start bg-white px-4 text-primary lg:hidden"
						>
							<div class="flex w-full items-center gap-5">
								<Skeleton is-active variant="white" class="size-[28px]" />
								<Skeleton is-active variant="white" class="h-5 flex-1" />
							</div>
						</div>
					</div>

					<section
						id="map-section"
						data-listing-map-controls-offset
						:class="mapSectionClasses"
					>
						<ClientOnly fallback-tag="div">
							<template #fallback>
								<div
									class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
								></div>
							</template>
							<div
								class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
							>
								<GmapMap
									v-show="shouldShowMap"
									id="wishlist-map"
									ref="gmapRef"
									:map-id="'wishlist-map'"
									:center="mapState.mapConfig.center"
									:bounds="mapState.mapConfig.bounds"
									:zoom="mapState.mapConfig.zoom"
									:min-zoom="mapState.mapConfig.minZoom"
									:max-zoom="mapState.mapConfig.maxZoom"
									:options="mapOptions"
									:clickable-icons="false"
									class="relative size-full !h-full touch-none"
									@drag-end="mapController.handleDragEnd"
									@zoom-changed="mapController.handleZoomChanged"
								>
									<GmapClusterMarker
										v-for="cluster in readyClusters"
										:key="getClusterKey(cluster.geometry.id)"
										:ref="(el: Element | ComponentPublicInstance | null) => setClusterMarkerRef(cluster.geometry.id, el as InstanceType<typeof GmapClusterMarker> | null)"
										:min-zoom="mapState.mapConfig.minZoom"
										:max-zoom="mapState.mapConfig.maxZoom"
										:cluster="cluster"
										:map="gmapRef?.mapInstance"
										:selected-from-parent="
											selectedClustersData.has(cluster.geometry.id)
										"
										:cluster-sizes="
											mapController.clusterSizes.value.get(cluster.geometry.id)
										"
										:selected-clusters-count="selectedClustersData.size"
										:is-favorite="props.showFavoriteIcons"
										@cluster-selection-changed="handleClusterSelectionChanged"
										@cluster-deselected-by-resolution-change="
											handleClusterDeselectedByResolution
										"
										@cluster-pan-zoom="handleClusterClicked"
										@clear-selected-clusters-before-zoom="
											handleClearSelectedClusters
										"
									/>
									<GmapMarker
										v-if="markerPosition"
										:position="markerPosition"
										:variant="'red'"
										size="default"
										:address="''"
										:gmp-clickable="false"
										:gmp-draggable="false"
										:is-favorite="props.showFavoriteIcons"
									/>

									<template #visible>
										<div
											class="absolute right-4 top-4 flex items-center gap-4 md:right-6 md:top-6 [&_button]:px-4 [&_button]:py-2"
										>
											<Button
												v-if="selectedClustersData.size > 0"
												variant="primary"
												class="size-min h-10 md:h-11"
												size="sm"
												@click="handleClearSelectedClusters"
											>
												Limpar seleção
												<span class="size-5 rounded-full bg-primary-light-3">
													{{ selectedClustersData.size }}
												</span>
											</Button>
										</div>
									</template>
								</GmapMap>
							</div>
						</ClientOnly>
					</section>
				</ContainerFluid>
			</div>
		</div>

		<!-- Share Collection Modal -->
		<ShareCollectionModal
			v-model="isShareModalOpen"
			:wishlist-id="props.wishlistId || ''"
			:properties="properties"
			:is-owner="isOwnWishlist"
			:owner-name="ownerName"
			:section-name="props.sectionName"
		/>
	</div>
</template>
